import React from "react";
import { MdSave, MdSettings } from "react-icons/md";
import { toastError, toastSuccess } from "lib/toast";
import {
  adminSupportRequestsService,
  type AdminSupportContactSettings,
} from "services/adminSupportRequestsService";

const DEFAULT_SETTINGS: AdminSupportContactSettings = {
  button_label_ar: "هل واجهت مشكلة؟ تواصل معنا",
  button_label_en: "Having a problem? Contact us",
  button_subtitle_ar: "أرسل تفاصيل المشكلة إلى الإدارة مع المرفقات",
  button_subtitle_en: "Send the problem details and attachments to admin",
  name_label_ar: "الاسم",
  name_label_en: "Name",
  name_placeholder_ar: "اكتب اسمك",
  name_placeholder_en: "Your name",
  phone_label_ar: "رقم الهاتف",
  phone_label_en: "Phone number",
  phone_placeholder_ar: "رقم يمكن التواصل عليه",
  phone_placeholder_en: "A number we can reach",
  problem_label_ar: "المشكلة",
  problem_label_en: "Problem",
  problem_placeholder_ar: "اشرح المشكلة بالتفصيل",
  problem_placeholder_en: "Describe the problem",
  attachments_label_ar: "مرفقات (اختياري، حتى 3 ملفات)",
  attachments_label_en: "Attachments (optional, up to 3 files)",
  attachments_button_ar: "اختيار ملفات",
  attachments_button_en: "Choose files",
  submit_label_ar: "إرسال للإدارة",
  submit_label_en: "Send to admin",
  sending_label_ar: "جارٍ الإرسال…",
  sending_label_en: "Sending…",
};

type SettingsField = {
  key: keyof AdminSupportContactSettings;
  label: string;
  dir: "rtl" | "ltr";
  maxLength: number;
};

export default function SupportContactSettingsPanel({ isArabic }: { isArabic: boolean }) {
  const [form, setForm] = React.useState<AdminSupportContactSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const settings = await adminSupportRequestsService.getSettings();
      setForm({ ...DEFAULT_SETTINGS, ...settings });
    } catch {
      toastError(isArabic ? "تعذر تحميل نصوص نموذج الدعم." : "Could not load support form text.");
    } finally {
      setLoading(false);
    }
  }, [isArabic]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const updateField = (key: keyof AdminSupportContactSettings, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function save() {
    if (Object.values(form).some((value) => !value.trim())) {
      toastError(isArabic ? "اكتب كل النصوص قبل الحفظ." : "Fill in all texts before saving.");
      return;
    }

    setSaving(true);
    try {
      const settings = await adminSupportRequestsService.updateSettings(form);
      setForm({ ...DEFAULT_SETTINGS, ...settings });
      toastSuccess(isArabic ? "تم حفظ نصوص نموذج الدعم." : "Support form text saved.");
    } catch {
      toastError(isArabic ? "تعذر حفظ نصوص نموذج الدعم." : "Could not save support form text.");
    } finally {
      setSaving(false);
    }
  }

  const field = (
    key: keyof AdminSupportContactSettings,
    label: string,
    dir: "rtl" | "ltr",
    maxLength = 160,
  ): SettingsField => ({ key, label, dir, maxLength });

  const headerFields: SettingsField[] = [
    field("button_label_ar", isArabic ? "عنوان الصفحة بالعربي" : "Page title (Arabic)", "rtl"),
    field("button_label_en", isArabic ? "عنوان الصفحة بالإنجليزي" : "Page title (English)", "ltr"),
    field("button_subtitle_ar", isArabic ? "وصف الصفحة بالعربي" : "Page description (Arabic)", "rtl", 300),
    field("button_subtitle_en", isArabic ? "وصف الصفحة بالإنجليزي" : "Page description (English)", "ltr", 300),
  ];
  const formFields: SettingsField[] = [
    field("name_label_ar", isArabic ? "عنوان الاسم بالعربي" : "Name label (Arabic)", "rtl"),
    field("name_label_en", isArabic ? "عنوان الاسم بالإنجليزي" : "Name label (English)", "ltr"),
    field("name_placeholder_ar", isArabic ? "Placeholder الاسم بالعربي" : "Name placeholder (Arabic)", "rtl"),
    field("name_placeholder_en", isArabic ? "Placeholder الاسم بالإنجليزي" : "Name placeholder (English)", "ltr"),
    field("phone_label_ar", isArabic ? "عنوان الهاتف بالعربي" : "Phone label (Arabic)", "rtl"),
    field("phone_label_en", isArabic ? "عنوان الهاتف بالإنجليزي" : "Phone label (English)", "ltr"),
    field("phone_placeholder_ar", isArabic ? "Placeholder الهاتف بالعربي" : "Phone placeholder (Arabic)", "rtl"),
    field("phone_placeholder_en", isArabic ? "Placeholder الهاتف بالإنجليزي" : "Phone placeholder (English)", "ltr"),
    field("problem_label_ar", isArabic ? "عنوان المشكلة بالعربي" : "Problem label (Arabic)", "rtl"),
    field("problem_label_en", isArabic ? "عنوان المشكلة بالإنجليزي" : "Problem label (English)", "ltr"),
    field("problem_placeholder_ar", isArabic ? "Placeholder المشكلة بالعربي" : "Problem placeholder (Arabic)", "rtl"),
    field("problem_placeholder_en", isArabic ? "Placeholder المشكلة بالإنجليزي" : "Problem placeholder (English)", "ltr"),
    field("attachments_label_ar", isArabic ? "عنوان المرفقات بالعربي" : "Attachments label (Arabic)", "rtl"),
    field("attachments_label_en", isArabic ? "عنوان المرفقات بالإنجليزي" : "Attachments label (English)", "ltr"),
    field("attachments_button_ar", isArabic ? "زر اختيار الملفات بالعربي" : "Choose-files button (Arabic)", "rtl"),
    field("attachments_button_en", isArabic ? "زر اختيار الملفات بالإنجليزي" : "Choose-files button (English)", "ltr"),
  ];
  const actionFields: SettingsField[] = [
    field("submit_label_ar", isArabic ? "زر الإرسال بالعربي" : "Submit button (Arabic)", "rtl"),
    field("submit_label_en", isArabic ? "زر الإرسال بالإنجليزي" : "Submit button (English)", "ltr"),
    field("sending_label_ar", isArabic ? "نص الإرسال بالعربي" : "Sending text (Arabic)", "rtl"),
    field("sending_label_en", isArabic ? "نص الإرسال بالإنجليزي" : "Sending text (English)", "ltr"),
  ];

  const renderFields = (fields: SettingsField[]) => (
    <div className="grid gap-4 md:grid-cols-2">
      {fields.map((item) => (
        <label key={item.key} className="block text-sm font-semibold text-slate-200">
          {item.label}
          <textarea
            dir={item.dir}
            rows={2}
            maxLength={item.maxLength}
            value={form[item.key]}
            onChange={(event) => updateField(item.key, event.target.value)}
            className="ef-input-field mt-2 resize-y"
          />
        </label>
      ))}
    </div>
  );

  return (
    <div className="ef-panel p-5" dir={isArabic ? "rtl" : "ltr"}>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2 text-white">
            <MdSettings className="h-5 w-5 text-brand-300" />
            <h2 className="text-base font-bold">{isArabic ? "محتوى نموذج التواصل" : "Contact form content"}</h2>
          </div>
          <p className="mt-1 text-xs leading-6 text-slate-400">
            {isArabic
              ? "تحكم في النصوص التي تظهر للطالب في زر الدعم وصفحة التواصل وحقول النموذج."
              : "Control the text shown to students in the support button, contact page, and form fields."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={loading || saving}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-brand-600 disabled:cursor-wait disabled:opacity-60"
        >
          <MdSave className="h-5 w-5" />
          {saving ? (isArabic ? "جاري الحفظ…" : "Saving…") : isArabic ? "حفظ النصوص" : "Save text"}
        </button>
      </div>

      {loading ? (
        <p className="mt-5 text-sm text-slate-400">{isArabic ? "جارٍ تحميل الإعدادات…" : "Loading settings…"}</p>
      ) : (
        <div className="mt-5 space-y-6">
          <section>
            <h3 className="mb-3 text-sm font-bold text-brand-200">{isArabic ? "عنوان ووصف الصفحة" : "Page title and description"}</h3>
            {renderFields(headerFields)}
          </section>
          <section>
            <h3 className="mb-3 text-sm font-bold text-brand-200">{isArabic ? "حقول النموذج" : "Form fields"}</h3>
            {renderFields(formFields)}
          </section>
          <section>
            <h3 className="mb-3 text-sm font-bold text-brand-200">{isArabic ? "أزرار النموذج" : "Form actions"}</h3>
            {renderFields(actionFields)}
          </section>
        </div>
      )}
    </div>
  );
}
